/**
 * HRCandidates — live candidate list from /v1/hr/candidates with filters, a
 * detail drawer, and shortlist/reject actions. When a job is selected (drill-down
 * from HRJobPostings) the list is filtered to that job's role.
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical P1 design system).
 */
import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X, Check, Plus, Upload } from 'lucide-react'
import { SectionTitle, Avatar, LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import { usePolledAsync, fetchCandidates, shortlistCandidate, rejectCandidate, ingestCV, createCandidate } from '../../api'
import { useWriteAccess } from '../../../../lib/writeAccess'
import type { ApiHRCandidate, ApiHRJobPosting } from '../../types'
import { apiErrorMessage } from '../../../../api/client'
import { toast } from 'sonner'
import { danger, ink, overlay, statusCandidate, success, surface, warning } from '../../../../theme/tokens'

function scoreColor(score?: number): string {
  if (!score) return ink.muted
  if (score >= 80) return success.DEFAULT
  if (score >= 60) return warning.DEFAULT
  return danger.DEFAULT
}

const STATUS_BADGE: Record<string, { bg: string; color: string }> = {
  pending_review: { bg: statusCandidate.pending_review.bg, color: statusCandidate.pending_review.text },
  shortlisted:    { bg: statusCandidate.shortlisted.bg,    color: statusCandidate.shortlisted.text },
  interviewing:   { bg: statusCandidate.interviewing.bg,   color: statusCandidate.interviewing.text },
  offered:        { bg: statusCandidate.offered.bg,        color: statusCandidate.offered.text },
  rejected:       { bg: statusCandidate.rejected.bg,       color: statusCandidate.rejected.text },
  hired:          { bg: statusCandidate.hired.bg,          color: statusCandidate.hired.text },
}

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_BADGE[status] ?? { bg: surface.panel, color: ink.secondary }
  return (
    <span className="k-badge" style={{ background: cfg.bg, color: cfg.color }}>
      <span className="k-badge-dot" />{status.replace(/_/g, ' ').toUpperCase()}
    </span>
  )
}

// The k-btn file-picker (the slice-4 UploadInvoiceButton precedent): the
// shared UploadButton keeps its DashButton chrome for any other consumer;
// markup-level k-* reuse lives with the department.
function UploadCVButton({ onFile }: { onFile: (base64: string) => Promise<void> }) {
  const [busy, setBusy] = useState(false)
  const ref = useRef<HTMLInputElement>(null)
  async function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setBusy(true)
    try {
      const b64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          const result = String(reader.result)
          const comma = result.indexOf(',')
          resolve(comma >= 0 ? result.slice(comma + 1) : result)
        }
        reader.onerror = () => reject(reader.error ?? new Error('file read failed'))
        reader.readAsDataURL(file)
      })
      await onFile(b64)
    } finally {
      setBusy(false)
      if (ref.current) ref.current.value = '' // allow re-uploading the same file
    }
  }
  return (
    <>
      <input ref={ref} type="file" accept="application/pdf" className="hidden" onChange={handleChange} />
      <button className="k-btn k-btn--lg inline-flex items-center gap-2" onClick={() => ref.current?.click()}>
        <Upload size={14} /> {busy ? 'Uploading…' : 'Upload CV'}
      </button>
    </>
  )
}

const STATUSES = ['all', 'pending_review', 'shortlisted', 'interviewing', 'offered', 'hired', 'rejected'] as const

// ── Detail drawer ───────────────────────────────────────────────────────────────

function CandidateDrawer({ candidate, onClose, onShortlist, onReject }: {
  candidate: ApiHRCandidate
  onClose: () => void
  /** Absent for a role without records access: no footer actions. */
  onShortlist?: (id: string) => void
  onReject?: (id: string) => void
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="k-drawer"
        style={{ width: 'min(560px, 100vw)', animation: 'slide-in-right 220ms cubic-bezier(0.23,1,0.32,1)' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0 border-b border-edge-row">
          <div className="flex items-center gap-3">
            <Avatar name={candidate.fullName} size={40} />
            <div>
              <div className="text-detail-title font-bold text-ink-primary">{candidate.fullName}</div>
              <div className="text-12 text-ink-muted">{candidate.roleApplied || '—'}</div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-control text-ink-muted hover:bg-surface-nav-hover transition-colors">
            <X size={16} />
          </button>
        </div>

        <div className="flex items-center gap-3 px-6 py-4 border-b border-edge-row flex-shrink-0">
          <StatusBadge status={candidate.status} />
          <div className="flex items-center gap-2">
            <span className="text-11 text-ink-muted">AI score</span>
            <span className="text-16 font-bold" style={{ color: scoreColor(candidate.aiScore) }}>{candidate.aiScore ?? '—'}</span>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-4">
          {candidate.aiSummary && (
            <div>
              <div className="k-eyebrow uppercase mb-1">AI Summary</div>
              <p className="text-13 text-ink-body leading-relaxed">{candidate.aiSummary}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 text-13">
            <div><div className="text-11 text-ink-muted">Email</div><div className="text-ink-primary">{candidate.email || '—'}</div></div>
            <div><div className="text-11 text-ink-muted">Phone</div><div className="text-ink-primary">{candidate.phone || '—'}</div></div>
            <div><div className="text-11 text-ink-muted">Location</div><div className="text-ink-primary">{candidate.location || '—'}</div></div>
            <div><div className="text-11 text-ink-muted">Experience</div><div className="text-ink-primary">{candidate.experienceYears ? `${candidate.experienceYears} yrs` : '—'}</div></div>
            <div><div className="text-11 text-ink-muted">Source</div><div className="text-ink-primary capitalize">{candidate.source || '—'}</div></div>
            <div><div className="text-11 text-ink-muted">Assigned To</div><div className="text-ink-primary">{candidate.assignedTo || '—'}</div></div>
            <div><div className="text-11 text-ink-muted">Education</div><div className="text-ink-primary">{candidate.education || '—'}</div></div>
            <div><div className="text-11 text-ink-muted">Job Interests</div><div className="text-ink-primary">{candidate.jobInterests || '—'}</div></div>
            <div><div className="text-11 text-ink-muted">Current Salary</div><div className="text-ink-primary">{candidate.currentSalary || '—'}</div></div>
            <div><div className="text-11 text-ink-muted">Address</div><div className="text-ink-primary">{candidate.address || '—'}</div></div>
          </div>
          {candidate.skills && candidate.skills.length > 0 && (
            <div>
              <div className="k-eyebrow uppercase mb-2">Skills</div>
              <div className="flex flex-wrap gap-2">
                {candidate.skills.map((s, i) => (
                  <span key={i} className="k-badge bg-surface-panel text-ink-secondary">{s.toUpperCase()}</span>
                ))}
              </div>
            </div>
          )}
          {candidate.cvText && (
            <details>
              <summary className="k-eyebrow uppercase cursor-pointer">CV Text</summary>
              <p className="text-12 text-ink-secondary leading-relaxed mt-2 whitespace-pre-wrap">{candidate.cvText}</p>
            </details>
          )}
        </div>

        {/* Footer actions */}
        {onShortlist && onReject && <div className="flex items-center gap-2 px-6 pt-5 pb-14 border-t border-edge-row flex-shrink-0">
          <button onClick={() => onShortlist(candidate.id)}
            className="k-btn k-btn--lg flex-1 inline-flex items-center justify-center gap-2 text-ink-on-accent"
            style={{ background: success.DEFAULT }}>
            <Check size={14} /> Shortlist
          </button>
          <button onClick={() => onReject(candidate.id)}
            className="k-btn k-btn--lg flex-1 inline-flex items-center justify-center gap-2 text-ink-on-accent"
            style={{ background: danger.DEFAULT }}>
            <X size={14} /> Reject
          </button>
        </div>}
      </div>
    </div>,
    document.body
  )
}

// ── New Candidate Drawer ────────────────────────────────────────────────────────

function NewCandidateDrawer({ onClose, onCreated }: {
  onClose: () => void
  onCreated: (candidate: ApiHRCandidate) => void
}) {
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [roleApplied, setRoleApplied] = useState('')
  const [experience, setExperience] = useState('')
  const [education, setEducation] = useState('')
  const [jobInterests, setJobInterests] = useState('')
  const [currentSalary, setCurrentSalary] = useState('')
  const [address, setAddress] = useState('')
  const [currentEmployer, setCurrentEmployer] = useState('')
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [niNumber, setNiNumber] = useState('')

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  async function handleSave() {
    if (!fullName.trim()) { toast.error('Full name is required'); return }
    try {
      const created = await createCandidate({
        fullName: fullName.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        roleApplied: roleApplied.trim() || undefined,
        experienceYears: experience.trim() ? Number(experience) : undefined,
        source: 'manual',
        education: education.trim() || undefined,
        jobInterests: jobInterests.trim() || undefined,
        currentSalary: currentSalary.trim() || undefined,
        address: address.trim() || undefined,
        currentEmployer: currentEmployer.trim() || undefined,
        dateOfBirth: dateOfBirth || undefined,
        nationalInsuranceNumber: niNumber.trim() || undefined,
      })
      onCreated(created)
      toast.success('Candidate created')
      onClose()
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to create candidate'))
    }
  }

  const inputCls = "w-full px-4 py-3 bg-surface-control border border-edge-control rounded-control text-13 text-ink-body placeholder:text-ink-muted transition-all"
  const labelCls = "block text-12 font-semibold text-ink-body mb-2"

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="k-drawer"
        style={{ width: 'min(560px, 100vw)', animation: 'slide-in-right 220ms cubic-bezier(0.23,1,0.32,1)' }}>
        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0 border-b border-edge-row">
          <div>
            <div className="text-detail-title font-bold text-ink-primary">New Candidate</div>
            <div className="text-12 text-ink-muted mt-1">Add a candidate to your pipeline</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-control text-ink-muted hover:bg-surface-nav-hover transition-colors">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
          <div>
            <label className={labelCls}>Full Name *</label>
            <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="e.g. Jane Doe" autoFocus className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="jane@example.com" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Contact Number</label>
              <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+44 …" className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Role Applied</label>
              <input type="text" value={roleApplied} onChange={e => setRoleApplied(e.target.value)} placeholder="e.g. Support Worker" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Work Experience (years)</label>
              <input type="number" min="0" value={experience} onChange={e => setExperience(e.target.value)} placeholder="e.g. 3" className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Education</label>
            <input type="text" value={education} onChange={e => setEducation(e.target.value)} placeholder="e.g. BSc Social Work, University of Leeds" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Job Interests</label>
            <input type="text" value={jobInterests} onChange={e => setJobInterests(e.target.value)} placeholder="e.g. Youth work, welfare-to-work" className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Current Salary</label>
              <input type="text" value={currentSalary} onChange={e => setCurrentSalary(e.target.value)} placeholder="e.g. £28,000" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Address</label>
              <input type="text" value={address} onChange={e => setAddress(e.target.value)} placeholder="e.g. 12 High St, Leeds LS1" className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Current Employer</label>
            <input type="text" value={currentEmployer} onChange={e => setCurrentEmployer(e.target.value)} placeholder="e.g. Leeds City Council" className={inputCls} />
          </div>

          {/* Payroll & funding — deliberately grouped APART from the
              recruitment fields: these are collected for funding and payroll
              set-up, not assessment, and the form should show that. */}
          <div className="pt-4 border-t border-edge-row">
            <div className="k-eyebrow uppercase">Payroll &amp; funding details</div>
            <div className="text-12 text-ink-muted mt-1 mb-4">Used for funding and payroll set-up only — not part of the assessment.</div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Date of Birth</label>
                <input type="date" value={dateOfBirth} onChange={e => setDateOfBirth(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>National Insurance Number</label>
                <input type="text" value={niNumber} onChange={e => setNiNumber(e.target.value)} placeholder="QQ 12 34 56 C" className={inputCls} />
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 px-6 pt-5 pb-14 border-t border-edge-row flex-shrink-0">
          <button onClick={onClose} className="k-btn-secondary">
            Cancel
          </button>
          <button onClick={handleSave} className="k-btn k-btn--lg">
            Create Candidate
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

interface HRCandidatesProps {
  selectedJob?: ApiHRJobPosting
  onBack?: () => void
}

export default function HRCandidates({ selectedJob, onBack }: HRCandidatesProps = {}) {
  const { data, loading, error } = usePolledAsync(fetchCandidates, 10_000, [])
  const [candidates, setCandidates] = useState<ApiHRCandidate[]>([])
  const [statusFilter, setStatusFilter] = useState<(typeof STATUSES)[number]>('all')
  const [search, setSearch] = useState('')
  const [detail, setDetail] = useState<ApiHRCandidate | null>(null)
  const [showNew, setShowNew] = useState(false)
  // Create, CV ingest, shortlist and reject are records-level HR writes
  // (lib/writeAccess; fail closed).
  const { records } = useWriteAccess()

  useEffect(() => { if (data) setCandidates(data) }, [data])

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const filtered = candidates.filter(c => {
    if (selectedJob && c.roleApplied !== selectedJob.title) return false
    if (statusFilter !== 'all' && c.status !== statusFilter) return false
    if (search && !c.fullName.toLowerCase().includes(search.toLowerCase()) &&
        !(c.roleApplied ?? '').toLowerCase().includes(search.toLowerCase())) return false
    return true
  })

  async function applyStatus(id: string, action: 'shortlist' | 'reject') {
    try {
      const updated = action === 'shortlist' ? await shortlistCandidate(id) : await rejectCandidate(id)
      setCandidates(prev => prev.map(c => c.id === id ? updated : c))
      setDetail(null)
      toast.success(action === 'shortlist' ? 'Candidate shortlisted' : 'Candidate rejected')
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Action failed'))
    }
  }

  return (
    <div className="page-enter flex flex-col gap-4">
      <SectionTitle
        title={selectedJob ? `Candidates — ${selectedJob.title}` : 'Candidates'}
        subtitle={`${filtered.length} candidate${filtered.length !== 1 ? 's' : ''}`}
        action={
          <div className="flex items-center gap-2">
            {onBack && <button className="k-btn-secondary" onClick={onBack}>← Back to job postings</button>}
            {records && <UploadCVButton
              onFile={async (b64) => {
                try {
                  await ingestCV(b64, selectedJob?.title)
                  toast.success('CV uploaded — screening in progress. The candidate will appear once processed.')
                } catch (e) {
                  toast.error(apiErrorMessage(e, 'Upload failed'))
                }
              }}
            />}
            {records && <button className="k-btn k-btn--lg inline-flex items-center gap-2" onClick={() => setShowNew(true)}><Plus size={14} /> New Candidate</button>}
          </div>
        }
      />

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        {STATUSES.map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3 py-2 rounded-full text-12 font-medium transition-colors capitalize ${statusFilter === s ? 'bg-ink-primary text-ink-on-accent border border-ink-primary' : 'bg-surface-control text-ink-secondary hover:bg-surface-nav-hover border border-edge-control hover:border-edge-control-hover'}`}>
            {s === 'all' ? 'All' : s.replace(/_/g, ' ')}
          </button>
        ))}
        <div className="ml-auto">
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search name, role…"
            className="h-8 px-3 text-12 bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-muted focus:outline-none focus:border-accent w-56" />
        </div>
      </div>

      <div className="k-card">
        {filtered.length === 0 ? (
          <EmptyState message="No candidates match your filter" />
        ) : (
          <table className="closr-table hr-table w-full">
            <thead>
              <tr>
                <th className="text-left">Name</th>
                <th className="text-left">Role Applied</th>
                <th className="text-left">AI Score</th>
                <th className="text-left">Source</th>
                <th className="text-left">Status</th>
                <th className="text-left">Assigned To</th>
                <th className="text-left">Applied</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id} className="cursor-pointer" onClick={() => setDetail(c)}>
                  <td>
                    <div className="flex items-center gap-3">
                      <Avatar name={c.fullName} size={26} />
                      <span className="text-13 font-medium text-ink-primary">{c.fullName}</span>
                    </div>
                  </td>
                  <td className="text-12 text-ink-secondary">{c.roleApplied || '—'}</td>
                  <td><span className="text-12 font-semibold" style={{ color: scoreColor(c.aiScore) }}>{c.aiScore ?? '—'}</span></td>
                  <td className="text-12 text-ink-secondary capitalize">{c.source || '—'}</td>
                  <td><StatusBadge status={c.status} /></td>
                  <td className="text-12 text-ink-secondary">{c.assignedTo || '—'}</td>
                  <td className="text-12 text-ink-muted">{new Date(c.createdAt).toLocaleDateString('en-GB')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {detail && (
        <CandidateDrawer
          candidate={detail}
          onClose={() => setDetail(null)}
          onShortlist={records ? (id) => applyStatus(id, 'shortlist') : undefined}
          onReject={records ? (id) => applyStatus(id, 'reject') : undefined}
        />
      )}
      {showNew && records && (
        <NewCandidateDrawer
          onClose={() => setShowNew(false)}
          onCreated={(c) => { setCandidates(prev => [c, ...prev]); setShowNew(false) }}
        />
      )}
    </div>
  )
}
